import { useEffect, useMemo, useState } from 'react';
import { Save, Eye, EyeOff, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { SALES_ROLES, STAFF_CONSOLE_ROLES, ADMIN_ROLE } from '../../config/roles';
import { effectivePermissions } from '../../utils/workspace';

const ALL_PERMISSIONS = [
    { group: 'Admin', items: [
        { key: 'admin.dashboard',      label: 'Dashboard' },
        { key: 'admin.approvals',      label: 'Approvals' },
        { key: 'admin.users',          label: 'Users' },
        { key: 'admin.access',         label: 'Access & permissions' },
        { key: 'admin.downloads',      label: 'Downloads (admin)' },
        { key: 'admin.incentives',     label: 'Incentives (admin)' },
        { key: 'admin.reimbursements', label: 'Reimbursements (admin)' },
        { key: 'admin.achievers',      label: 'Banner update' },
        { key: 'admin.hero-slides',    label: 'Hero Slides' },
        { key: 'admin.franchises',     label: 'Franchise manager' },
        { key: 'admin.form-builder',   label: 'Form builder' },
        { key: 'admin.clients',        label: 'All clients' },
        { key: 'admin.payment-ledger', label: 'Payment ledger' },
        { key: 'admin.settings',       label: 'Settings' },
        { key: 'admin.kpi-analytics',  label: 'KPI analytics' },
        { key: 'admin.aoi-calculator', label: 'AOI calculator' },
        { key: 'admin.aoi-location',   label: 'AOI location' },
        { key: 'admin.refunds',        label: 'Refunds' },
        { key: 'admin.zoho-leads',     label: 'Zoho leads' },
        { key: 'admin.activity',       label: 'Activity log' },
    ]},
    { group: 'Sales', items: [
        { key: 'sales.dashboard',      label: 'Dashboard' },
        { key: 'sales.kpi-analytics',  label: 'KPI analytics' },
        { key: 'sales.clients',        label: 'My clients' },
        { key: 'sales.legal',          label: 'Legal' },
        { key: 'sales.clients.new',    label: 'Register client' },
        { key: 'sales.downloads',      label: 'Downloads' },
        { key: 'sales.incentives',     label: 'Incentives' },
        { key: 'sales.reimbursements', label: 'Reimbursements' },
        { key: 'sales.leads',          label: 'My leads' },
        { key: 'sales.team-leads',     label: 'Team leads' },
        { key: 'sales.my-team',        label: 'My Team' },
    ]},
    { group: 'Client actions', items: [
        { key: 'action.loi-stage.update', label: 'Update LOI stage' },
        { key: 'action.agreement-stage.update', label: 'Update Agreement stage' },
    ]},
];

const PERM_LABEL = Object.fromEntries(
    ALL_PERMISSIONS.flatMap(g => g.items.map(i => [i.key, i.label])),
);

function matchesAudience(u, audience) {
    if (audience === 'sales') return SALES_ROLES.has(u.role);
    return u.role === ADMIN_ROLE || STAFF_CONSOLE_ROLES.has(u.role);
}

/**
 * Per-user page permissions — sales staff or admin staff.
 * @param {'sales'|'admin'} audience
 */
export default function Permissions({ audience = 'sales' }) {
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [picked, setPicked] = useState(null);
    const [perms, setPerms] = useState([]);
    const [q, setQ] = useState('');
    const [busy, setBusy] = useState(false);

    const loadUsers = async () => {
        const { data } = await api.get('/users', { params: { status: 'active' } });
        const items = (data.items || []).filter(u => matchesAudience(u, audience));
        setUsers(items);
        return items;
    };

    useEffect(() => {
        (async () => {
            const [items] = await Promise.all([
                loadUsers(),
                api.get('/roles').then(r => setRoles(r.data.items || [])).catch(() => {}),
            ]);
            if (items.length) {
                setPicked(items[0]);
                setPerms(items[0].permissions || []);
            } else {
                setPicked(null);
                setPerms([]);
            }
        })();
        /* eslint-disable-next-line */
    }, [audience]);

    const filtered = useMemo(() => users.filter(u =>
        !q || u.name.toLowerCase().includes(q.toLowerCase()) || u.email.toLowerCase().includes(q.toLowerCase())
    ), [users, q]);

    const roleDoc = useMemo(
        () => roles.find(r => r.slug === picked?.role),
        [roles, picked?.role],
    );

    const isLocked = !!roleDoc?.isSuperUser;

    const roleDefaultPerms = roleDoc?.permissions || [];

    const effectivePerms = useMemo(() => {
        if (!picked) return [];
        return effectivePermissions(picked, roleDoc);
    }, [picked, roleDoc]);

    const hasUserOverride = (picked?.permissions || []).length > 0;

    const pickUser = (u) => {
        setPicked(u);
        setPerms(u.permissions || []);
    };

    const toggle = (key) => {
        if (isLocked) return toast('Super-user roles cannot be restricted per user');
        setPerms(p => p.includes(key) ? p.filter(x => x !== key) : [...p, key]);
    };

    const allOf = (group) => {
        if (isLocked) return;
        const keys = group.items.map(i => i.key);
        const allOn = keys.every(k => perms.includes(k));
        setPerms(allOn ? perms.filter(p => !keys.includes(p)) : [...new Set([...perms, ...keys])]);
    };

    const save = async () => {
        if (!picked || isLocked) return;
        setBusy(true);
        try {
            await api.put(`/users/${picked._id}/permissions`, { permissions: perms });
            toast.success(`Permissions saved for ${picked.name}`);
            setUsers(u => u.map(x => x._id === picked._id ? { ...x, permissions: perms } : x));
            setPicked({ ...picked, permissions: perms });
        } finally {
            setBusy(false);
        }
    };

    const audienceLabel = audience === 'sales' ? 'sales' : 'admin / operations';

    return (
        <div className="space-y-4">
            <p className="text-sm text-ink-500">
                Pick a {audienceLabel} user to view their effective access and update per-user page overrides.
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="ag-card p-3 lg:max-h-[640px] overflow-y-auto">
                    <div className="relative mb-3">
                        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-500" />
                        <input className="ag-input pl-9" placeholder="Search users…" value={q} onChange={e => setQ(e.target.value)} />
                    </div>
                    <div className="space-y-1">
                        {filtered.map(u => (
                            <button
                                key={u._id}
                                type="button"
                                onClick={() => pickUser(u)}
                                className={`w-full text-left p-3 rounded-lg transition border
                                ${picked?._id === u._id ? 'bg-brand-teal-50 border-brand-teal/40' : 'border-transparent hover:bg-ink-100'}`}
                            >
                                <div className="font-semibold text-sm">{u.name}</div>
                                <div className="text-xs text-ink-500">{u.email}</div>
                                <div className="text-[10px] uppercase tracking-wider text-ink-500 mt-0.5">{u.role}</div>
                            </button>
                        ))}
                        {filtered.length === 0 && (
                            <div className="text-sm text-ink-500 p-4 text-center">No {audienceLabel} users.</div>
                        )}
                    </div>
                </div>

                <div className="lg:col-span-2 ag-card p-5">
                    {!picked ? (
                        <div className="text-ink-500">Pick a user.</div>
                    ) : (
                        <>
                            <div className="flex items-end justify-between flex-wrap gap-3">
                                <div>
                                    <div className="font-bold">{picked.name}</div>
                                    <div className="text-xs text-ink-500">{picked.email} · {picked.role}</div>
                                    {roleDoc && (
                                        <div className="text-xs text-ink-500 mt-1">Role: {roleDoc.name}</div>
                                    )}
                                    {isLocked && (
                                        <div className="mt-2 text-xs bg-amber-50 text-amber-800 inline-flex items-center gap-2 px-2 py-1 rounded-lg border border-amber-200">
                                            <EyeOff className="h-3 w-3" /> Super-user — full access (edit role instead)
                                        </div>
                                    )}
                                </div>
                                <button type="button" className="ag-btn-primary" disabled={busy || isLocked} onClick={save}>
                                    <Save className="h-4 w-4" /> {busy ? 'Saving…' : 'Save permissions'}
                                </button>
                            </div>

                            <div className="mt-4 rounded-xl bg-ink-50 ring-1 ring-ink-100 p-4 space-y-3">
                                <div>
                                    <div className="text-[10px] font-bold uppercase tracking-wider text-ink-500">
                                        Effective access ({effectivePerms.length} pages)
                                    </div>
                                    <p className="text-[11px] text-ink-500 mt-0.5">
                                        {hasUserOverride
                                            ? 'Using per-user override below (replaces role default).'
                                            : roleDefaultPerms.length
                                                ? 'From role default (no per-user override).'
                                                : 'No pages assigned — user sees an empty menu until a role or override is set.'}
                                    </p>
                                    <div className="flex flex-wrap gap-1.5 mt-2 max-h-28 overflow-y-auto">
                                        {effectivePerms.length === 0 ? (
                                            <span className="text-xs text-ink-500">All pages (legacy / unrestricted)</span>
                                        ) : effectivePerms.map(key => (
                                            <span key={key} className="text-[10px] px-2 py-0.5 rounded-full bg-white ring-1 ring-ink-200 text-ink-700">
                                                {PERM_LABEL[key] || key}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                                {roleDefaultPerms.length > 0 && (
                                    <div>
                                        <div className="text-[10px] font-bold uppercase tracking-wider text-ink-500">
                                            Role default ({roleDefaultPerms.length})
                                        </div>
                                        <div className="flex flex-wrap gap-1 mt-1">
                                            {roleDefaultPerms.slice(0, 12).map(key => (
                                                <span key={key} className="text-[10px] px-1.5 py-0.5 rounded bg-ink-100 text-ink-600">
                                                    {PERM_LABEL[key] || key}
                                                </span>
                                            ))}
                                            {roleDefaultPerms.length > 12 && (
                                                <span className="text-[10px] text-ink-500">+{roleDefaultPerms.length - 12} more</span>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="mt-5 space-y-5">
                                <div className="text-sm font-semibold text-ink-800">
                                    Per-user override {hasUserOverride ? '(active)' : '(empty — uses role default)'}
                                </div>
                                {(audience === 'sales'
                                    ? ALL_PERMISSIONS.filter(g => g.group === 'Sales' || g.group === 'Client actions')
                                    : ALL_PERMISSIONS
                                ).map(group => (
                                    <div key={group.group}>
                                        <div className="flex items-center justify-between mb-2">
                                            <h4 className="text-sm font-bold uppercase tracking-wider text-ink-500">
                                                {group.group === 'Client actions' ? 'Client actions' : `${group.group} pages`}
                                            </h4>
                                            <button type="button" className="text-xs ag-link" onClick={() => allOf(group)} disabled={isLocked}>
                                                Toggle all
                                            </button>
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                            {group.items.map(item => {
                                                const on = perms.includes(item.key);
                                                const fromRole = !hasUserOverride && roleDefaultPerms.includes(item.key);
                                                return (
                                                    <button
                                                        key={item.key}
                                                        type="button"
                                                        onClick={() => toggle(item.key)}
                                                        disabled={isLocked}
                                                        className={`flex items-center justify-between gap-2 p-3 rounded-lg border transition text-left
                                                        ${on ? 'border-brand-teal/40 bg-brand-teal-50' : 'border-ink-300 hover:border-ink-500/30'}
                                                        ${isLocked ? 'opacity-60 cursor-not-allowed' : ''}
                                                        ${fromRole && !on ? 'ring-1 ring-ink-200 bg-ink-50/50' : ''}`}
                                                    >
                                                        <span className="flex items-center gap-2 text-sm min-w-0">
                                                            {on ? <Eye className="h-4 w-4 shrink-0 text-brand-teal-700" /> : <EyeOff className="h-4 w-4 shrink-0 text-ink-500" />}
                                                            <span className="truncate">{item.label}</span>
                                                        </span>
                                                        <span className="text-[10px] font-mono text-ink-500 shrink-0">{item.key}</span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                                <div className="text-xs text-ink-500">
                                    Empty override = role default. Saving toggles replaces the role template for this user only.
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
